"use client";

import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import React from 'react';
import { ApiPlan, getPlanPrice } from '@/services/apiPlansService';
import { colors, typography } from '@/theme/colors';

// Plan tier order for upgrade/downgrade logic (lowest to highest)
const PLAN_TIER_ORDER: Record<string, number> = {
  'api-free': 0,
  'api-starter': 1,
  'api-pro': 2,
  'api-business': 3
};
interface ApiPlanCardProps {
  plan: ApiPlan;
  isCurrentPlan: boolean;
  isPopular?: boolean;
  onUpgrade: (planSlug: string) => void;
  loading?: boolean;
  disabled?: boolean;
  index: number;
  currentPlanSlug?: string | null;
}
const ApiPlanCard: React.FC<ApiPlanCardProps> = ({
  plan,
  isCurrentPlan,
  isPopular = false,
  onUpgrade,
  loading = false,
  disabled = false,
  index,
  currentPlanSlug = null
}) => {
  const t = useTranslations('apiPlans');
  const price = getPlanPrice(plan);
  const handleClick = () => {
    if (!isCurrentPlan && !loading && !disabled) {
      onUpgrade(plan.slug);
    }
  };
  const formatCooldown = (hours: number): string => {
    if (hours >= 1) return `${hours}h`;
    return `${Math.round(hours * 60)}min`;
  };

  // Determine upgrade/downgrade status
  const currentTier = currentPlanSlug ? PLAN_TIER_ORDER[currentPlanSlug] ?? -1 : -1;
  const targetTier = PLAN_TIER_ORDER[plan.slug] ?? 0;
  const isDowngrade = currentTier > 0 && targetTier < currentTier;
  const isUpgrade = targetTier > currentTier;
  const isFreeUser = currentTier <= 0; // No key or free plan

  // Button state based on comparison
  const isButtonDisabled = isCurrentPlan || loading || disabled || isDowngrade;
  const getButtonText = () => {
    if (loading) return null; // Will show spinner
    if (isCurrentPlan) return t('currentPlan');
    if (isDowngrade) return t('downgradeDisabled');
    return t('upgradeToX', {
      plan: plan.name.replace('API ', '')
    });
  };
  return <Box component="article" role="listitem" aria-current={isCurrentPlan ? 'true' : undefined} sx={{
    position: 'relative',
    bgcolor: colors.background.paper,
    border: isCurrentPlan ? `3px solid ${colors.status.success.main}` : `1px solid ${colors.border.light}`,
    borderRadius: '12px',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    animation: 'fadeInUp 0.3s ease-out forwards',
    animationDelay: `${index * 50}ms`,
    opacity: 0,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': isCurrentPlan ? {} : {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    },
    '@keyframes fadeInUp': {
      from: {
        opacity: 0,
        transform: 'translateY(8px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)'
      }
    }
  }} data-sentry-element="Box" data-sentry-component="ApiPlanCard" data-sentry-source-file="ApiPlanCard.tsx">
      <Box sx={{
      position: 'absolute',
      top: -12,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 1
    }} data-sentry-element="Box" data-sentry-source-file="ApiPlanCard.tsx">
        {isCurrentPlan && <Chip icon={<CheckIcon sx={{
        fontSize: 14
      }} />} label={t('currentPlan')} size="small" sx={{
        bgcolor: colors.status.success.main,
        color: 'white',
        fontWeight: 600,
        fontSize: '11px',
        height: 24,
        '& .MuiChip-icon': {
          color: 'white'
        }
      }} />}
        {isPopular && !isCurrentPlan && <Chip icon={<StarIcon sx={{
        fontSize: 14
      }} />} label={t('mostPopular')} size="small" sx={{
        bgcolor: colors.primary.main,
        color: 'white',
        fontWeight: 600,
        fontSize: '11px',
        height: 24,
        '& .MuiChip-icon': {
          color: 'white'
        }
      }} />}
      </Box>

      <Typography sx={{
      ...typography.presets.cardTitle,
      color: colors.text.primary,
      mt: isCurrentPlan || isPopular ? 1.5 : 0
    }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
        {plan.name}
      </Typography>

      <Box sx={{
      display: 'flex',
      alignItems: 'baseline',
      mt: 1.5,
      mb: 2
    }} data-sentry-element="Box" data-sentry-source-file="ApiPlanCard.tsx">
        <Typography sx={{
        fontSize: '28px',
        fontWeight: 700,
        lineHeight: 1.2,
        color: colors.text.primary
      }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
          ${price}
        </Typography>
        <Typography sx={{
        ...typography.presets.caption,
        color: colors.text.tertiary,
        ml: 0.5
      }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
          {t('perMonth')}
        </Typography>
      </Box>

      <Box sx={{
      height: '1px',
      bgcolor: colors.border.light,
      mb: 2
    }} data-sentry-element="Box" data-sentry-source-file="ApiPlanCard.tsx" />

      <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }} data-sentry-element="Box" data-sentry-source-file="ApiPlanCard.tsx">
        <Typography sx={{
        ...typography.presets.bodySecondary,
        color: colors.text.secondary,
        fontSize: {
          xs: '14px',
          sm: '13px'
        }
      }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
          • {t('features.quota', {
          count: plan.quota.toLocaleString()
        })}
        </Typography>
        <Typography sx={{
        ...typography.presets.bodySecondary,
        color: colors.text.secondary,
        fontSize: {
          xs: '14px',
          sm: '13px'
        }
      }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
          • {t('features.cooldown', {
          hours: formatCooldown(plan.cooldownHours)
        })}
        </Typography>
        <Typography sx={{
        ...typography.presets.bodySecondary,
        color: colors.text.secondary,
        fontSize: {
          xs: '14px',
          sm: '13px'
        }
      }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
          • {t('features.concurrency', {
          count: plan.maxConcurrency
        })}
        </Typography>
        <Typography sx={{
        ...typography.presets.bodySecondary,
        color: colors.text.secondary,
        fontSize: {
          xs: '14px',
          sm: '13px'
        }
      }} data-sentry-element="Typography" data-sentry-source-file="ApiPlanCard.tsx">
          • {t('features.rateLimit', {
          count: plan.rateLimit
        })}
        </Typography>
      </Box>

      <Button variant={isCurrentPlan ? 'outlined' : 'contained'} fullWidth disabled={isButtonDisabled} onClick={handleClick} sx={{
      mt: 3,
      textTransform: 'none',
      minHeight: 44,
      bgcolor: isCurrentPlan || isDowngrade ? 'transparent' : colors.primary.main,
      color: isCurrentPlan || isDowngrade ? colors.text.tertiary : 'white',
      borderColor: isCurrentPlan || isDowngrade ? colors.border.medium : undefined,
      transition: 'all 0.15s ease',
      '&:hover': {
        bgcolor: isCurrentPlan || isDowngrade ? 'transparent' : colors.primary.hover
      },
      '&:active': {
        transform: 'scale(0.98)'
      },
      '&.Mui-disabled': {
        bgcolor: isCurrentPlan || isDowngrade ? 'transparent' : undefined,
        color: isCurrentPlan || isDowngrade ? colors.text.tertiary : undefined
      }
    }} data-sentry-element="Button" data-sentry-source-file="ApiPlanCard.tsx">
        {loading ? <CircularProgress size={20} color="inherit" /> : getButtonText()}
      </Button>
    </Box>;
};
export default ApiPlanCard;